/** Briques d'interface partagées. */
import type { ReactNode } from 'react';
import type { HitStatus, PollStatus } from '@vigie/shared';

export function Card({
  title,
  actions,
  children,
  tight,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  tight?: boolean;
}) {
  return (
    <section className="card">
      {(title || actions) && (
        <header className="card-header">
          {typeof title === 'string' ? <h2>{title}</h2> : title}
          <span className="spacer" />
          {actions}
        </header>
      )}
      <div className={tight ? 'card-body tight' : 'card-body'}>{children}</div>
    </section>
  );
}

export function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: ReactNode }) {
  return (
    <div className="card stat">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {hint ? <div className="hint">{hint}</div> : null}
    </div>
  );
}

export function Score({ score }: { score: number | null }) {
  if (score === null) return <span className="score low" title="Pas encore scoré">—</span>;
  const level = score >= 7 ? 'high' : score >= 4 ? 'mid' : 'low';
  return (
    <span className={`score ${level}`} title={`Pertinence ${score}/10`}>
      {score}
    </span>
  );
}

const STATUS_LABEL: Record<HitStatus, { label: string; className: string }> = {
  new: { label: 'nouveau', className: 'accent' },
  relevant: { label: 'pertinent', className: 'ok' },
  ignored: { label: 'ignoré', className: '' },
  drafted: { label: 'brouillon', className: 'warn' },
  posted: { label: 'publié', className: 'ok' },
};

export function StatusBadge({ status }: { status: HitStatus }) {
  const item = STATUS_LABEL[status];
  return <span className={`badge ${item.className}`}>{item.label}</span>;
}

const POLL_LABEL: Record<PollStatus, string> = {
  ok: 'ok',
  error: 'erreur',
  skipped: 'ignoré',
  rate_limited: 'quota atteint',
};

export function PollBadge({ status }: { status: PollStatus }) {
  const className =
    status === 'ok' ? 'ok' : status === 'error' ? 'danger' : status === 'rate_limited' ? 'warn' : '';
  return <span className={`badge ${className}`}>{POLL_LABEL[status]}</span>;
}

export function Notice({
  kind = 'info',
  children,
}: {
  kind?: 'info' | 'error' | 'success' | 'rule';
  children: ReactNode;
}) {
  return <div className={kind === 'info' ? 'notice' : `notice ${kind}`}>{children}</div>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function Field({
  label,
  help,
  children,
}: {
  label: string;
  help?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="field">
      <label>{label}</label>
      {children}
      {help ? <div className="help">{help}</div> : null}
    </div>
  );
}

/**
 * Courbe des découvertes. SVG calculé à la main : aucune dépendance graphique
 * pour un graphique à deux séries.
 */
export function Chart({ points }: { points: { day: string; discovered: number; kept: number }[] }) {
  if (points.length < 2) {
    return <Empty>Pas encore assez de données pour tracer une courbe.</Empty>;
  }
  const width = 800;
  const height = 140;
  const padding = 8;
  const max = Math.max(1, ...points.map((point) => point.discovered));
  const step = (width - padding * 2) / (points.length - 1);
  const y = (value: number) => height - padding - (value / max) * (height - padding * 2);
  const path = (key: 'discovered' | 'kept') =>
    points.map((point, index) => `${index === 0 ? 'M' : 'L'}${padding + index * step},${y(point[key])}`).join(' ');

  return (
    <svg className="chart" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" role="img"
         aria-label="Découvertes par jour">
      <line className="grid-line" x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} />
      <path className="area" d={`${path('discovered')} L${width - padding},${height - padding} L${padding},${height - padding} Z`} />
      <path className="line" d={path('discovered')} />
      <path className="line kept" d={path('kept')} />
    </svg>
  );
}
